/**
 * useCart Hook - Enhanced with Inventory Validation Error Handling
 * Based on your current useCart.js with added inventory validation features
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import cartService from '../services/cartServices.js';
import authService from '../services/authService.js';
import { STORE_CREDITS_KEY } from './useStoreCredits';

// Query Keys
const QUERY_KEYS = {
  CART: ['cart'],
  CART_VALIDATION: ['cart', 'validation']
};

export const useCart = () => {
  const queryClient = useQueryClient();
  const [userFeedback, setUserFeedback] = useState(null); // NEW: For showing user-friendly messages
  
  // Get current store info — employees never have a store, so cart is naturally disabled
  const currentStore = authService.getCurrentStore();
  const isAuthenticated = authService.isAuthenticated();
  const hasStore = !!(isAuthenticated && currentStore?.id && !authService.isEmployee());

  // Cart data query - only enabled when user has selected a store
  const { 
    data: cartData, 
    isLoading: isLoadingCart, 
    error: cartError 
  } = useQuery({
    queryKey: QUERY_KEYS.CART,
    queryFn: () => cartService.getDraftCartOrder(),
    enabled: hasStore, // Only fetch cart when user has a store selected
    staleTime: 1000, // Very short stale time for frequent updates
    cacheTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: false, // Don't auto-refetch, rely on mutations
    retry: (failureCount, error) => {
      // Don't retry if no store selected
      if (error?.message?.includes('No store selected')) return false;
      return failureCount < 2;
    },
    onError: (error) => {
      console.error('Cart query error:', error);
    }
  });

  // Enhanced add to cart mutation with inventory error handling
  const addToCartMutation = useMutation({
    mutationFn: (cartItem) => cartService.addToCart(cartItem),
    onMutate: async (newItem) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries(QUERY_KEYS.CART);
      const previousCart = queryClient.getQueryData(QUERY_KEYS.CART);
      setUserFeedback(null); // Clear any previous feedback

      // Optimistically update to the new value
      if (previousCart) {
        const optimisticCart = { ...previousCart };
        const existingItemIndex = optimisticCart.draftCartOrderItems?.findIndex(
          item => item.productSkuVariantID === newItem.productSkuVariantID
        );

        if (existingItemIndex >= 0) {
          // Update existing item
          optimisticCart.draftCartOrderItems[existingItemIndex] = {
            ...optimisticCart.draftCartOrderItems[existingItemIndex],
            unitQuantity: optimisticCart.draftCartOrderItems[existingItemIndex].unitQuantity + newItem.unitQuantity
          };
        } else {
          // Add new item
          const productData = newItem.productSkuVariant || {};
          const newCartItem = {
            id: -Date.now(), // Temporary ID
            productSkuVariantID: newItem.productSkuVariantID,
            unitQuantity: newItem.unitQuantity,
            unitPrice: newItem.unitPrice,
            singleUnitFee: newItem.singleUnitFee?.toString() || "0",
            isSample: newItem.isSample || false,
            productSkuVariant: {
              ...productData,
              // Ensure key properties are preserved for calculator
              unitsPerCase: productData?.unitsPerCase || 1,
              optSellByTypeID: productData?.optSellByTypeID || 1,
              unitAvailable: productData?.unitAvailable || 0,
              unitPrice: productData?.unitPrice || newItem.unitPrice || 0,
              singleUnitFee: productData?.singleUnitFee || newItem.singleUnitFee || 0,
            }
          };
          optimisticCart.draftCartOrderItems = [
            ...(optimisticCart.draftCartOrderItems || []),
            newCartItem
          ];
        }

        queryClient.setQueryData(QUERY_KEYS.CART, optimisticCart);
      }

      // Return rollback function
      return { previousCart };
    },
    onSuccess: (updatedCart) => {
      // The cart service now returns properly merged data
      queryClient.setQueryData(QUERY_KEYS.CART, updatedCart);
      setUserFeedback({
        type: 'success',
        message: 'Item added to cart successfully'
      });
    },
    onError: (error, variables, context) => {
      console.error('Add to cart error:', error);
      
      // NEW: Handle inventory validation errors
      if (error.message?.includes('Only ') && error.message?.includes('units available')) {
        setUserFeedback({
          type: 'warning',
          message: error.message,
          productId: variables.productSkuVariantID,
          action: 'inventory_limit'
        });
      } else {
        setUserFeedback({
          type: 'error',
          message: 'Failed to add item to cart. Please try again.',
          action: 'retry'
        });
      }

      // Rollback to previous state
      if (context?.previousCart) {
        queryClient.setQueryData(QUERY_KEYS.CART, context.previousCart);
      }
    }
  });

  // Enhanced update product quantity mutation (for product tiles)
  const updateProductQuantityMutation = useMutation({
    mutationFn: ({ productSkuVariantId, newQuantity, productData }) => 
      cartService.updateProductQuantityInCart(productSkuVariantId, newQuantity, productData),
    onMutate: async ({ productSkuVariantId, newQuantity, productData }) => {
      await queryClient.cancelQueries(QUERY_KEYS.CART);
      const previousCart = queryClient.getQueryData(QUERY_KEYS.CART);
      setUserFeedback(null); // Clear any previous feedback

      if (previousCart) {
        const optimisticCart = { ...previousCart };
        const existingItemIndex = optimisticCart.draftCartOrderItems?.findIndex(
          item => item.productSkuVariantID === productSkuVariantId
        );

        if (newQuantity <= 0) {
          // Remove item
          if (existingItemIndex >= 0) {
            optimisticCart.draftCartOrderItems = optimisticCart.draftCartOrderItems.filter(
              item => item.productSkuVariantID !== productSkuVariantId
            );
          }
        } else if (existingItemIndex >= 0) {
          // Update existing item
          optimisticCart.draftCartOrderItems[existingItemIndex] = {
            ...optimisticCart.draftCartOrderItems[existingItemIndex],
            unitQuantity: newQuantity
          };
        } else if (newQuantity > 0) {
          // Add new item - use productSkuVariantId as temporary ID to avoid negative issues
          const newCartItem = {
            id: `temp_${productSkuVariantId}`, // Use string ID to avoid confusion
            productSkuVariantID: productSkuVariantId,
            unitQuantity: newQuantity,
            unitPrice: productData?.unitPrice || 0,
            singleUnitFee: productData?.singleUnitFee?.toString() || "0",
            isSample: false,
            productSkuVariant: {
              ...productData,
              // Ensure key properties are preserved for calculator
              unitsPerCase: productData?.unitsPerCase || 1,
              optSellByTypeID: productData?.optSellByTypeID || 1,
              unitAvailable: productData?.unitAvailable || 0,
              unitPrice: productData?.unitPrice || 0,
              singleUnitFee: productData?.singleUnitFee || 0,
            }
          };
          optimisticCart.draftCartOrderItems = [
            ...(optimisticCart.draftCartOrderItems || []),
            newCartItem
          ];
        }

        queryClient.setQueryData(QUERY_KEYS.CART, optimisticCart);
      }

      return { previousCart, productSkuVariantId, requestedQuantity: newQuantity };
    },
    onSuccess: (updatedCart, variables) => {
      // Cart service now properly merges data
      queryClient.setQueryData(QUERY_KEYS.CART, updatedCart);
      
      if (variables.newQuantity > 0) {
        setUserFeedback({
          type: 'success',
          message: 'Cart updated successfully'
        });
      }
    },
    onError: (error, variables, context) => {
      console.error('Update product quantity error:', error);
      
      // NEW: Handle inventory validation errors with specific product context
      if (error.message?.includes('Only ') && error.message?.includes('units available')) {
        const availableMatch = error.message.match(/Only (\d+) units available/);
        const availableUnits = availableMatch ? parseInt(availableMatch[1]) : null;
        
        setUserFeedback({
          type: 'warning',
          message: error.message,
          productId: context?.productSkuVariantId,
          availableQuantity: availableUnits,
          requestedQuantity: context?.requestedQuantity,
          action: 'inventory_adjusted'
        });
      } else {
        setUserFeedback({
          type: 'error',
          message: 'Failed to update cart. Please try again.',
          productId: context?.productSkuVariantId,
          action: 'retry'
        });
      }

      if (context?.previousCart) {
        queryClient.setQueryData(QUERY_KEYS.CART, context.previousCart);
      }
    }
  });

  // Enhanced update cart item quantity mutation with better error handling
  const updateCartItemMutation = useMutation({
    mutationFn: ({ itemId, quantity }) => {
      console.log(`REACT QUERY: updateCartItemMutation called with itemId=${itemId}, quantity=${quantity}`);
      return cartService.updateCartItemQuantity(itemId, quantity);
    },
    onMutate: async ({ itemId, quantity }) => {
      console.log(`REACT QUERY: onMutate called with itemId=${itemId}, quantity=${quantity}`);
      await queryClient.cancelQueries(QUERY_KEYS.CART);
      const previousCart = queryClient.getQueryData(QUERY_KEYS.CART);
      setUserFeedback(null); // Clear any previous feedback

      // Don't do optimistic updates here since cartService handles them
      console.log('REACT QUERY: Skipping optimistic update, letting cartService handle it');

      return { previousCart, itemId, requestedQuantity: quantity };
    },
    onSuccess: (updatedCart, variables) => {
      console.log(`REACT QUERY: updateCartItem onSuccess`, { variables, hasUpdatedCart: !!updatedCart });
      // Cart service now properly merges data
      if (updatedCart) {
        queryClient.setQueryData(QUERY_KEYS.CART, updatedCart);
      } else {
        // Fallback: invalidate to force refresh if no updated cart returned
        queryClient.invalidateQueries(QUERY_KEYS.CART);
      }
      
      setUserFeedback({
        type: 'success',
        message: 'Cart item updated successfully'
      });
    },
    onError: (error, variables, context) => {
      console.error('Update cart item error:', error, { variables });
      
      // NEW: Handle inventory validation errors
      if (error.message?.includes('Only ') && error.message?.includes('units available')) {
        const availableMatch = error.message.match(/Only (\d+) units available/);
        const availableUnits = availableMatch ? parseInt(availableMatch[1]) : null;
        
        setUserFeedback({
          type: 'warning',
          message: error.message,
          itemId: context?.itemId,
          availableQuantity: availableUnits,
          requestedQuantity: context?.requestedQuantity,
          action: 'inventory_adjusted'
        });
      } else {
        setUserFeedback({
          type: 'error',
          message: 'Failed to update item quantity. Please try again.',
          itemId: context?.itemId,
          action: 'retry'
        });
      }

      if (context?.previousCart) {
        queryClient.setQueryData(QUERY_KEYS.CART, context.previousCart);
      } else {
        // If we don't have previous cart, just invalidate to get fresh state
        queryClient.invalidateQueries(QUERY_KEYS.CART);
      }
    },
    onSettled: () => {
      // Ensure we have the latest cart state after mutation settles
      console.log('REACT QUERY: updateCartItem mutation settled');
    }
  });

  // Remove from cart mutation
  const removeFromCartMutation = useMutation({
    mutationFn: (itemId) => cartService.removeFromCart(itemId),
    onMutate: async (itemId) => {
      await queryClient.cancelQueries(QUERY_KEYS.CART);
      const previousCart = queryClient.getQueryData(QUERY_KEYS.CART);
      setUserFeedback(null);

      if (previousCart) {
        const optimisticCart = { ...previousCart };
        optimisticCart.draftCartOrderItems = optimisticCart.draftCartOrderItems?.filter(
          item => item.id !== itemId
        ) || [];

        queryClient.setQueryData(QUERY_KEYS.CART, optimisticCart);
      }

      return { previousCart };
    },
    onSuccess: (updatedCart) => {
      queryClient.setQueryData(QUERY_KEYS.CART, updatedCart);
      setUserFeedback({
        type: 'info',
        message: 'Item removed from cart'
      });
    },
    onError: (error, variables, context) => {
      console.error('Remove from cart error:', error);
      setUserFeedback({
        type: 'error',
        message: 'Failed to remove item from cart. Please try again.'
      });

      if (context?.previousCart) {
        queryClient.setQueryData(QUERY_KEYS.CART, context.previousCart);
      }
    }
  });

  // Clear cart mutation
  const clearCartMutation = useMutation({
    mutationFn: () => cartService.clearCart(),
    onMutate: async () => {
      await queryClient.cancelQueries(QUERY_KEYS.CART);
      const previousCart = queryClient.getQueryData(QUERY_KEYS.CART);
      setUserFeedback(null);

      if (previousCart) {
        const optimisticCart = { 
          ...previousCart, 
          draftCartOrderItems: [] 
        };
        queryClient.setQueryData(QUERY_KEYS.CART, optimisticCart);
      }

      return { previousCart };
    },
    onSuccess: (updatedCart) => {
      queryClient.setQueryData(QUERY_KEYS.CART, updatedCart);
      setUserFeedback({
        type: 'info',
        message: 'Cart cleared successfully'
      });
    },
    onError: (error, variables, context) => {
      console.error('Clear cart error:', error);
      setUserFeedback({
        type: 'error',
        message: 'Failed to clear cart. Please try again.'
      });

      if (context?.previousCart) {
        queryClient.setQueryData(QUERY_KEYS.CART, context.previousCart);
      }
    }
  });

  // Checkout mutation
  const checkoutMutation = useMutation({
    mutationFn: (checkoutData) => cartService.checkout(checkoutData),
    // Credits may have been spent (or, on a 409, spent elsewhere): refresh the store's balance either way,
    // for the header, the alert bar and the checkout picker.
    onSettled: () => queryClient.invalidateQueries({ queryKey: [STORE_CREDITS_KEY] }),
    onSuccess: (result) => {
      // Clear cart cache after successful checkout
      queryClient.removeQueries(QUERY_KEYS.CART);

      // Build success message based on response format
      let successMessage;
      if (result?.wasSplit && result?.orders?.length > 0) {
        // Split order response - multiple orders created
        const orderIds = result.orders.map(o => o?.id).filter(Boolean);
        if (orderIds.length > 1) {
          successMessage = `Orders #${orderIds.join(', #')} placed successfully!`;
        } else if (orderIds.length === 1) {
          successMessage = `Order #${orderIds[0]} placed successfully!`;
        } else {
          successMessage = result.message || 'Order placed successfully!';
        }
      } else if (result?.order?.id) {
        // Single order response
        successMessage = `Order #${result.order.id} placed successfully!`;
      } else {
        // Fallback
        successMessage = result?.message || 'Order placed successfully!';
      }

      setUserFeedback({
        type: 'success',
        message: successMessage
      });

      // Could also invalidate orders queries here
      // queryClient.invalidateQueries(['orders']);
    },
    onError: (error) => {
      console.error('Checkout error:', error);
      setUserFeedback({
        type: 'error',
        message: error.message || 'Checkout failed. Please try again.'
      });
    }
  });

  // Performance optimization: Memoize cart items to prevent unnecessary re-renders
  const cartItems = useMemo(() => {
    return cartData?.draftCartOrderItems || [];
  }, [cartData?.draftCartOrderItems]);

  // Performance optimization: Stable function references with useCallback
  const getCartItems = useCallback(() => {
    return cartItems;
  }, [cartItems]);

  // Performance optimization: Memoize totals calculation
  const totals = useMemo(() => {
    return cartService.calculateCartTotals(cartItems);
  }, [cartItems]);

  const getCartTotals = useCallback(() => {
    return totals;
  }, [totals]);

  // Money per brand ({ product, withFees }), for capping credits at checkout.
  const brandTotals = useMemo(() => cartService.calculateBrandTotals(cartItems), [cartItems]);
  // The cart total less samples: the most credits can cover, since no credit covers a sample.
  const creditableCartTotal = useMemo(
    () => cartService.calculateCartTotals(cartItems.filter((item) => !item.isSample)).total,
    [cartItems],
  );

  // NEW: Cart validation query - gets validation errors from service
  const { 
    data: validationResults, 
    isLoading: isValidating 
  } = useQuery({
    queryKey: QUERY_KEYS.CART_VALIDATION,
    queryFn: () => cartService.getValidationErrors(), // Get validation errors from service
    enabled: hasStore && !!cartData && cartItems.length > 0,
    staleTime: 10 * 1000, // 10 seconds
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
  });

  // Loading states (React Query v5 uses isPending instead of isLoading for mutations)
  const isLoading = isLoadingCart ||
                   addToCartMutation.isPending ||
                   updateCartItemMutation.isPending ||
                   removeFromCartMutation.isPending ||
                   clearCartMutation.isPending ||
                   checkoutMutation.isPending;

  // Error handling
  const error = cartError || 
               addToCartMutation.error || 
               updateCartItemMutation.error || 
               removeFromCartMutation.error || 
               clearCartMutation.error ||
               checkoutMutation.error;

  // Performance optimization: Stable store info function
  const getCurrentStoreInfo = useCallback(() => {
    try {
      return cartService.getCurrentStoreInfo();
    } catch (err) {
      return {
        hasStore: false,
        storeId: null,
        storeName: null,
        error: err.message
      };
    }
  }, [currentStore?.id]); // Only depends on store ID

  // Performance optimization: Stable utility functions
  const isItemInCart = useCallback((productSkuVariantId) => {
    return cartItems.some(item => item.productSkuVariantID === productSkuVariantId);
  }, [cartItems]);

  const getItemQuantityInCart = useCallback((productSkuVariantId) => {
    const item = cartItems.find(item => item.productSkuVariantID === productSkuVariantId);
    return item?.unitQuantity || 0;
  }, [cartItems]);

  const updateProductQuantity = useCallback((productSkuVariantId, newQuantity, productData) => {
    return updateProductQuantityMutation.mutateAsync({ 
      productSkuVariantId, 
      newQuantity, 
      productData 
    });
  }, [updateProductQuantityMutation.mutateAsync]);

  const getProductQuantityInCart = useCallback(async (productSkuVariantId) => {
    return getItemQuantityInCart(productSkuVariantId);
  }, [getItemQuantityInCart]);

  // Stable cart item update function
  const updateCartItemQuantity = useCallback((itemId, newQuantity) => {
    console.log(`HOOK: updateCartItemQuantity called with itemId=${itemId}, newQuantity=${newQuantity}`);
    return updateCartItemMutation.mutateAsync({ itemId, quantity: newQuantity });
  }, [updateCartItemMutation.mutateAsync]);

  // Performance optimization: Stable calculation functions
  const getCartSubtotal = useCallback(() => totals.subtotal || 0, [totals.subtotal]);
  const getCartFeeTotal = useCallback(() => totals.fees || 0, [totals.fees]);
  const getCartUnitTotal = useCallback(() => totals.unitTotal || 0, [totals.unitTotal]);
  const getCartTotal = useCallback(() => totals.total || 0, [totals.total]);
  const getCartItemCount = useCallback(() => totals.itemCount || 0, [totals.itemCount]);
  const getCartUnitCount = useCallback(() => totals.unitCount || 0, [totals.unitCount]);
  const getCartSavings = useCallback(() => totals.totalSavings || 0, [totals.totalSavings]);
  const getCartOriginalSubtotal = useCallback(() => totals.originalSubtotal || 0, [totals.originalSubtotal]);

  // Performance optimization: Stable refetch functions
  const refetchCart = useCallback(() => {
    return queryClient.invalidateQueries(QUERY_KEYS.CART);
  }, [queryClient]);

  const refetchValidation = useCallback(() => {
    return queryClient.invalidateQueries(QUERY_KEYS.CART_VALIDATION);
  }, [queryClient]);

  // NEW: Clear user feedback
  const clearUserFeedback = useCallback(() => {
    setUserFeedback(null);
  }, []);

  // NEW: Get inventory constraints for a product
  const getProductInventoryConstraints = useCallback((productSkuVariantId) => {
    const item = cartItems.find(item => item.productSkuVariantID === productSkuVariantId);
    if (!item?.productSkuVariant) return null;

    const product = item.productSkuVariant;
    return {
      unitAvailable: product.unitAvailable || 0,
      unitsPerCase: product.unitsPerCase || 1,
      optSellByTypeID: product.optSellByTypeID || 2, // Default 2 = case only
      maxCases: Math.floor((product.unitAvailable || 0) / (product.unitsPerCase || 1)),
      inStock: (product.unitAvailable || 0) > 0
    };
  }, [cartItems]);

  return {
    // Data
    cartData,
    cartItems,
    
    // Loading states (React Query v5 uses isPending instead of isLoading for mutations)
    isLoading,
    isLoadingCart,
    isAddingToCart: addToCartMutation.isPending,
    isUpdatingCart: updateCartItemMutation.isPending,
    isUpdatingProductQuantity: updateProductQuantityMutation.isPending,
    isRemovingFromCart: removeFromCartMutation.isPending,
    isClearingCart: clearCartMutation.isPending,
    isCheckingOut: checkoutMutation.isPending,
    isValidating,
    
    // Error states
    error,
    
    // NEW: User feedback system
    userFeedback,
    clearUserFeedback,
    
    // Actions
    addToCart: addToCartMutation.mutateAsync,
    updateCartItemQuantity, // Now properly passes through to mutation
    updateProductQuantity, // For product tiles
    removeFromCart: removeFromCartMutation.mutateAsync,
    clearCart: clearCartMutation.mutateAsync,
    checkout: checkoutMutation.mutateAsync,
    syncPendingChanges: cartService.syncPendingChanges, // Force sync
    
    // Performance optimized: Stable calculation functions
    getCartSubtotal,
    getCartFeeTotal,
    getCartUnitTotal,
    getCartTotal,
    getCartItemCount,
    getCartUnitCount,
    getCartSavings,
    getCartOriginalSubtotal,
    getCartTotals,
    brandTotals,
    creditableCartTotal,

    // Performance optimized: Stable utility functions
    isItemInCart,
    getItemQuantityInCart,
    getProductQuantityInCart,
    getCurrentStoreInfo,
    
    // NEW: Inventory constraints
    getProductInventoryConstraints,
    
    // Validation
    validationResults,
    hasValidationErrors: validationResults && validationResults.some(r => r.isRecent),
    
    // Store status
    hasStore,
    canUseCart: hasStore, 
    
    // Success states
    isCartUpdated: addToCartMutation.isSuccess || 
                   updateCartItemMutation.isSuccess || 
                   removeFromCartMutation.isSuccess ||
                   clearCartMutation.isSuccess,
    
    // Performance optimized: Stable refetch functions
    refetchCart,
    refetchValidation
  };
};

export default useCart;